import React, { useEffect, useState, useRef } from "react";
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { mapDispatchToHubObjectBoardProps } from 'store/actions/HubObjectBoardActions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBook, faBell } from '@fortawesome/free-solid-svg-icons';
import { toast, ToastContainer, Flip } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../../styles/main-content/Notifications.scss';

const NotificationPanel = (props) => {
    const [toastCount, setToastCount] = useState(0);
    const [isNotificationVisible, setNotificationVisible] = useState(false);
    const [newNotification, setNewNotification] = useState(false);
    const [showEmptyMessage, setShowEmptyMessage] = useState(true);
    const notificationRef = useRef(null);
    const toastsRef = useRef([]);

    useEffect(() => {
        let timeoutId;
        if (!!props.notificationObj) {
            const { folder, msg } = props.notificationObj;
            const notificationOptions = {
                autoClose: false,
                onClick: (toastId) => handleNotificationSelect(folder, toastId),
                onClose: handleNotificationClose,
                closeButton: true,
                className: 'notification-info',
                transition: Flip,
            };

            // Add toast and store the ID
            const toastId = toast(msg, notificationOptions);
            toastsRef.current.push(toastId);

            setToastCount(prevCount => prevCount + 1);
            setNewNotification(true);
            setShowEmptyMessage(false);

            timeoutId = setTimeout(() => {
                setNewNotification(false);
            }, 1000);
        }

        return () => {
            clearTimeout(timeoutId);
        };
    }, [props.notificationObj]);

    // Force update the empty message state when toast count or visibility changes
    useEffect(() => {
        updateEmptyMessageState();
    }, [toastCount, isNotificationVisible]);

    // Function to update empty message state
    const updateEmptyMessageState = () => {
        // Use a small delay to ensure DOM is updated
        setTimeout(() => {
            const toastElements = document.querySelectorAll('.Toastify__toast');
            if (toastElements.length === 0) {
                setShowEmptyMessage(true);
            } else {
                setShowEmptyMessage(false);
            }
        }, 50);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                isNotificationVisible &&
                notificationRef.current &&
                !notificationRef.current.contains(event.target)
            ) {
                setNotificationVisible(false);
            }
        };

        if (isNotificationVisible) {
            document.addEventListener('mousedown', handleClickOutside);
            updateEmptyMessageState();
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isNotificationVisible]);

    const handleNotificationClose = (toastId) => {
        // Remove the toast ID from our ref
        toastsRef.current = toastsRef.current.filter(id => id !== toastId);

        setToastCount(prevCount => {
            const newCount = Math.max(0, prevCount - 1);
            updateEmptyMessageState();
            return newCount;
        });
    };

    const handleNotificationSelect = async (folder, toastId) => {
        if (!folder) {
            return;
        }
        await props.getNewsItemsByFolder(folder.id);
        props.setWorkingFolder(folder);
        props.setActivePageName({ singular: "Story", plural: folder.name, icon: faBook });

        // Remove the toast from our tracking
        toastsRef.current = toastsRef.current.filter(id => id !== toastId);
        toast.dismiss(toastId);

        setToastCount(prevCount => Math.max(0, prevCount - 1));
        setNotificationVisible(false);
        updateEmptyMessageState();
    };

    const clearAllNotifications = () => {
        // Clear all tracked toasts
        toastsRef.current.forEach(id => toast.dismiss(id));
        toastsRef.current = [];

        setToastCount(0);
        setNewNotification(false);

        // Use setTimeout to ensure toasts are cleared before showing the message
        setTimeout(() => {
            setShowEmptyMessage(true);
        }, 100);
    };

    const toggleNotificationWindow = () => {
        setNotificationVisible(!isNotificationVisible);
        setNewNotification(false);
        if (!isNotificationVisible) {
            updateEmptyMessageState();
        }
    };

    return (
        <div className="notification-container" ref={notificationRef}>
            <button
                className={`notification-button ${newNotification ? 'new-notification' : ''}`}
                onClick={toggleNotificationWindow}
            >
                <FontAwesomeIcon icon={faBell} />
                {toastCount > 0 && (
                    <div className="notification-counter">{toastCount}</div>
                )}
            </button>
            <div className={`notification-window ${isNotificationVisible ? 'visible' : ''}`}>
                <button className="clear-all-button" onClick={clearAllNotifications}>
                    Clear All
                </button>
                <div className="notification-wrapper"></div>
                <div className="notifications-box">
                    {showEmptyMessage && (
                        <div className="no-notifications-message">
                            <p>No new notifications</p>
                        </div>
                    )}
                    <ToastContainer
                        position="bottom-right"
                        autoClose={false}
                        newestOnTop
                        hideProgressBar
                        draggable={false}
                        rtl={false}
                        style={{ position: 'sticky' }}
                        onOpen={() => {
                            setShowEmptyMessage(false);
                        }}
                        onClose={() => {
                            updateEmptyMessageState();
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

const mapNotificationsToProps = (state) => {
    return {
        notificationObj: state.hubObjectContent.notificationObj,
    };
};

export default connect(
    mapNotificationsToProps,
    dispatch => bindActionCreators({ ...mapDispatchToHubObjectBoardProps }, dispatch)
)(NotificationPanel);